import { saveBlob } from "../saveFile";
import { intl } from "../../containers/app";
import { roundNumber } from "./../consignmentFunctions";

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

/**
 * uloží seznam zásilek do xlsx
 * @param consignments seznam zásilek
 */
export function exportConsignmentDataToExcel(
  consignments: any[],
  tracking = false,
  sending = false
): void {
  createExcelFile().then((workbook) => {
    const sheet = workbook.addWorksheet("Consignments");
    sheet.columns = [
      {
        header: intl.formatMessage({
          id: "consignment.code",
          defaultMessage: "Číslo zásilky",
        }),
        key: "codes",
        width: 30,
      },
      {
        header: intl.formatMessage({
          id: "consignment.insertionDateTime",
          defaultMessage: "Datum založení",
        }),
        key: "insertionDateTime",
        width: 20,
      },
      {
        header: intl.formatMessage({
          id: "consignment.customerReference",
          defaultMessage: "Reference",
        }),
        key: "customerReference",
        width: 20,
      },
      {
        header: intl.formatMessage({
          id: "consignment.pickup",
          defaultMessage: "Nakládka",
        }),
        key: "pickup",
        width: 50,
      },
      {
        header: intl.formatMessage({
          id: "consignment.delivery",
          defaultMessage: "Vykládka",
        }),
        key: "delivery",
        width: 50,
      },
      {
        header: intl.formatMessage({
          id: "consignment.countOfCollis",
          defaultMessage: "Počet kusů",
        }),
        key: "countOfCollis",
        width: 10,
      },
      {
        header: intl.formatMessage({
          id: "consignment.cashOnDelivery",
          defaultMessage: "Dobírka",
        }),
        key: "cashOnDelivery",
        width: 25,
      },
      {
        header: intl.formatMessage({
          id: "consignment.insurance",
          defaultMessage: "Připojištění",
        }),
        key: "insurance",
        width: 15,
      },
      {
        header: intl.formatMessage({
          id: "consignment.sentDateTime",
          defaultMessage: "Odeslání dat",
        }),
        key: "sentDateTime",
        width: 20,
      },
      ...(sending && consignments.some((c) => c.transportPrice)
        ? [
            {
              header: intl.formatMessage(
                {
                  id: "calculator.priceWithoutVat",
                  defaultMessage: "{currency} bez DPH",
                },
                {
                  currency: intl.formatMessage({
                    id: "calculator.price",
                    defaultMessage: "Cena",
                  }),
                }
              ),
              key: "transportPrice",
              width: 15,
            },
            {
              header: intl.formatMessage({
                id: "invoice.currency",
                defaultMessage: "Měna",
              }),
              key: "transportPriceCurrency",
              width: 10,
            },
          ]
        : []),
      ...(tracking
        ? [
            {
              header: intl.formatMessage({
                id: "consignment.status",
                defaultMessage: "Status",
              }),
              key: "state",
              width: 10,
            },
            {
              header: intl.formatMessage({
                id: "consignment.lastTrackMessage",
                defaultMessage: "Stav zásilky",
              }),
              key: "lastTrackMessage",
              width: 75,
            },
          ]
        : []),
    ];

    sheet.addRows(
      consignments.map((c) => {
        return {
          codes: c.codes,
          insertionDateTime: c.insertionDateTime,
          customerReference: c.customerReference,
          transportPrice: c.transportPrice
            ? roundNumber(c.transportPrice)
            : "--",
          transportPriceCurrency: c.transportPrice
            ? c.transportPriceCurrency || "CZK"
            : "--",
          pickup: c.pickup,
          delivery: c.delivery,
          countOfCollis: c.countOfCollis,
          cashOnDelivery: c.cashOnDelivery,
          sentDateTime: c.sentDateTime,
          insurance: c.insurance,
          lastTrackMessage: c.lastTrackMessage,
          state: c.state,
        };
      })
    );

    workbook.xlsx.writeBuffer().then((buffer) => {
      saveBlob(new Blob([buffer]), "consignments.xlsx");
    });
  });
}

/**
 * uloží seznam adres do xlsx
 * @param addresses seznam adres
 */
export function exportAddressDataToExcel(addresses: any[]): void {
  createExcelFile().then((workbook) => {
    const sheet = workbook.addWorksheet("Customers");
    sheet.columns = [
      {
        header: intl.formatMessage({
          id: "address.name",
          defaultMessage: "Název",
        }),
        key: "name",
        width: 30,
      },
      {
        header: intl.formatMessage({
          id: "address.subname",
          defaultMessage: "Sekundární název",
        }),
        key: "subname",
        width: 20,
      },
      {
        header: intl.formatMessage({
          id: "address.street",
          defaultMessage: "Ulice",
        }),
        key: "street",
        width: 15,
      },
      {
        header: intl.formatMessage({
          id: "address.streetNumber",
          defaultMessage: "Číslo popisné",
        }),
        key: "streetNumber",
        width: 15,
      },
      {
        header: intl.formatMessage({
          id: "address.localDestination",
          defaultMessage: "Místní určení",
        }),
        key: "localDestination",
        width: 15,
      },
      {
        header: intl.formatMessage({
          id: "address.city",
          defaultMessage: "Město",
        }),
        key: "city",
        width: 20,
      },
      {
        header: intl.formatMessage({
          id: "address.zipCode",
          defaultMessage: "PSČ",
        }),
        key: "zipCode",
        width: 15,
      },
      {
        header: intl.formatMessage({
          id: "address.country",
          defaultMessage: "Stát",
        }),
        key: "country",
        width: 10,
      },
      {
        header: intl.formatMessage({
          id: "address.contactPerson",
          defaultMessage: "Kontaktní osoba",
        }),
        key: "contactPerson",
        width: 20,
      },
      {
        header: intl.formatMessage({
          id: "address.contactPhone",
          defaultMessage: "Telefon",
        }),
        key: "contactPhone",
        width: 20,
      },
      {
        header: intl.formatMessage({
          id: "address.contactEmail",
          defaultMessage: "Email",
        }),
        key: "contactEmail",
        width: 25,
      },
    ];

    sheet.addRows(
      addresses.map((a) => ({
        name: a.address.name,
        subname: a.address.subname,
        street: a.address.street,
        streetNumber: a.address.streetNumber,
        localDestination: a.address.localDestination,
        city: a.address.city,
        zipCode: a.address.zipCode,
        country: a.address.country.code,
        contactPerson: a.address.contactPerson,
        contactPhone: a.contactPhone !== "--" ? a.contactPhone : undefined,
        contactEmail: a.address.contactEmail,
      }))
    );

    workbook.xlsx.writeBuffer().then((buffer) => {
      saveBlob(new Blob([buffer]), "customers.xlsx");
    });
  });
}

/**
 * uloží seznam faktur do xlsx
 * @param addresses seznam faktur
 */
export function exportInvoiceDataToExcel(invoices: any[]): void {
  createExcelFile().then((workbook) => {
    const sheet = workbook.addWorksheet("Invoices");
    sheet.columns = [
      {
        header: intl.formatMessage({
          id: "invoice.code",
          defaultMessage: "Číslo",
        }),
        key: "code",
        width: 20,
      },
      {
        header: intl.formatMessage({
          id: "invoice.invoiceType",
          defaultMessage: "Druh",
        }),
        key: "invoiceType",
        width: 20,
      },
      {
        header: intl.formatMessage({
          id: "invoice.issueDate",
          defaultMessage: "Datum vystavení",
        }),
        key: "issueDate",
        width: 20,
      },
      {
        header: intl.formatMessage({
          id: "invoice.taxableDate",
          defaultMessage: "Datum zdanitelného plnění",
        }),
        key: "taxableDate",
        width: 30,
      },
      {
        header: intl.formatMessage({
          id: "invoice.dueDate",
          defaultMessage: "Datum splatnosti",
        }),
        key: "dueDate",
        width: 20,
      },
      {
        header: intl.formatMessage({
          id: "invoice.payDate",
          defaultMessage: "Datum úhrady",
        }),
        key: "payDate",
        width: 20,
      },
      {
        header: intl.formatMessage({
          id: "invoice.daysAfterDueDate",
          defaultMessage: "Počet dnů po splatnosti",
        }),
        key: "daysAfterDueDate",
        width: 22,
      },
      {
        header: intl.formatMessage({
          id: "invoice.amount",
          defaultMessage: "Částka",
        }),
        key: "amount",
        width: 15,
      },
      {
        header: intl.formatMessage({
          id: "invoice.currency",
          defaultMessage: "Měna",
        }),
        key: "currency",
        width: 10,
      },
    ];

    sheet.addRows(
      invoices.map((i) => ({
        code: i.invoice.code,
        invoiceType: i.invoiceType.name,
        issueDate: i.issueDate,
        taxableDate: i.taxableDate,
        dueDate: i.dueDate,
        payDate: i.payDate,
        daysAfterDueDate: i.invoice.daysAfterDueDate,
        amount: i.invoice.amount,
        currency: i.invoice.currency.code,
      }))
    );

    workbook.xlsx.writeBuffer().then((buffer) => {
      saveBlob(new Blob([buffer]), "invoices.xlsx");
    });
  });
}

/**
 * založí excel soubor
 */
async function createExcelFile() {
  const excel = await import("exceljs");
  const workbook = new excel.Workbook();
  workbook.creator = "DHL WebApp";
  workbook.lastModifiedBy = "DHL WebApp";
  workbook.created = new Date();
  workbook.modified = new Date();

  return workbook;
}
